import {
  AuthApi,
  OAuthCallbackParams,
  UserFinalizePayload,
  UserFinalizeResponse,
  UserInitiatePayload,
  UserInitiateResponse,
} from '../api';
import {InitParams, OldShortLink, SDKApiKeyResponse, ShortLink} from '../utils/types';
import {FetcherSettings} from '../api/apiClient';
import {MainTokenStorageHandler, GuestAccount, DecryptionConfig} from '../utils';

function isShortLink(data?: OldShortLink | ShortLink): data is ShortLink {
  return Boolean((data as ShortLink)?.instance_id);
}

const getMasterKey = (data?: SDKApiKeyResponse) => {
  const [masterKey, salt] = data?.build_hash?.split('.') || [];
  return {masterKey, salt};
};

type DecryptionConfigureCallback = (config: DecryptionConfig) => Promise<void>;

class AuthService {
  readonly authApi: AuthApi;
  private readonly configureDecryptionCallback?: DecryptionConfigureCallback;

  constructor(configureDecryptionCallback?: DecryptionConfigureCallback) {
    this.authApi = new AuthApi();
    this.configureDecryptionCallback = configureDecryptionCallback;
  }

  async init(params: InitParams): Promise<SDKApiKeyResponse>;

  async init(
    params: InitParams,
    resolverSettings?: FetcherSettings,
  ): Promise<SDKApiKeyResponse> {
    const payload = {
      apikey: params.apikey,
      resource_id: params.resourceId,
      external_resource_id: params.externalResourceId,
      resource_type: params.resourceType,
    };
    const data = await this.authApi.getAuthToken(payload, resolverSettings);

    if (params.encryptionEnabled && this.configureDecryptionCallback) {
      const {masterKey, salt} = getMasterKey(data);
      if (masterKey && salt) {
        await this.configureDecryptionCallback({
          enabled: true,
          masterKey,
          salt,
          jwtToken: data.access_token,
        }).catch(error => {
          console.error('Failed to configure decryption:', error);
        });
      }
    }

    if (data.access_token) {
      MainTokenStorageHandler.persistToken(data.access_token);
    }

    return data;
  }

  async initChekin(token: string, resolverSettings?: FetcherSettings) {
    const {body: data, headers} = await this.authApi.getChekinToken(
      {token},
      resolverSettings,
    );
    const headerToken = headers.get('Authorization');

    if (data) {
      MainTokenStorageHandler.persistToken(isShortLink(data) ? headerToken : data.user);
    }

    return data;
  }

  getIpDetails(resolverSettings?: FetcherSettings) {
    return this.authApi.fetchIpDetails(resolverSettings);
  }

  async initiateUser(
    payload: UserInitiatePayload,
    resolverSettings?: FetcherSettings,
  ): Promise<UserInitiateResponse> {
    return this.authApi.initiateUser(payload, resolverSettings);
  }

  async finalizeUser(
    payload: UserFinalizePayload,
    resolverSettings?: FetcherSettings,
  ): Promise<UserFinalizeResponse> {
    return this.authApi.finalizeUser(payload, resolverSettings);
  }

  async getGuestAccountToken(
    payload: UserInitiatePayload,
    resolverSettings?: FetcherSettings,
  ): Promise<UserFinalizeResponse> {
    return this.authApi.getToken(payload, resolverSettings);
  }

  async getMe(resolverSettings?: FetcherSettings): Promise<GuestAccount> {
    return this.authApi.getMe(resolverSettings);
  }

  async getParticipants(resolverSettings?: FetcherSettings) {
    return this.authApi.getParticipants(resolverSettings);
  }

  async oauthGoogleCallback(
    payload: OAuthCallbackParams,
    resolverSettings?: FetcherSettings,
  ) {
    return this.authApi.oauthGoogleCallback(payload, resolverSettings);
  }
}

export {AuthService};
